import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, Leaf, Wallet, FileSignature, Sparkles, ShieldCheck,
  TrendingUp, Banknote, Clock, Calculator, Users, BadgeCheck, Star, Award,
  CreditCard, Repeat, Building2, Lock,
} from "lucide-react";
import { Slider } from "../components/ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-b from-navy-900 via-navy-800 to-navy-900 text-white" data-testid="finance-hero">
    <div className="absolute inset-0 noise-bg opacity-30" />
    <div className="blob-pink absolute -top-32 -right-20 opacity-60" />
    <div className="absolute top-1/4 left-0 w-[500px] h-[500px] rounded-full bg-emerald-500/20 blur-[100px] pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="lg:col-span-7"
      >
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 mb-7">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-widest text-white">100% gefinancierde duurzaamheid</span>
        </div>

        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] text-balance" data-testid="finance-hero-title">
          Investeer in zon & opslag.{" "}
          <span className="gradient-pink-text">Betaal in maandtermijnen.</span>
        </h1>

        <p className="mt-7 text-lg text-slate-300 leading-relaxed max-w-xl">
          Met Helios Bright Duurzaam Financieren start je vandaag met zonnepanelen of een thuisbatterij, zonder eigen geld. Vaak is je maandbedrag lager dan je huidige besparing.
        </p>

        <div className="mt-9 flex flex-col sm:flex-row gap-4">
          <a href="#finance-calculator" className="btn-pink" data-testid="finance-hero-cta-1">
            Bereken uw maandbedrag <ArrowRight size={18} />
          </a>
          <Link to="/contact" state={{ calculator: { source: "finance_advies" } }} className="btn-ghost !bg-white/10 !text-white !border-white/20 hover:!bg-white/20" data-testid="finance-hero-cta-2">
            Vraag gratis advies aan
          </Link>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-5 max-w-md">
          <Stat tag="Vanaf" value="€ 38" sub="per maand" />
          <Stat tag="Looptijd" value="5 - 15" sub="jaar flexibel" />
          <Stat tag="Rente" value="vanaf 4,9%" sub="op jaarbasis" />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="lg:col-span-5"
      >
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-brand/30 to-emerald-500/30 blur-2xl rounded-3xl" />
          <div className="relative bg-white/5 backdrop-blur-xl border border-white/15 rounded-3xl p-7 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold">Voorbeeld</div>
                <div className="font-heading text-2xl font-black">12 panelen + 10 kWh batterij</div>
              </div>
              <div className="w-12 h-12 rounded-2xl gradient-pink flex items-center justify-center">
                <Wallet size={22} />
              </div>
            </div>

            <div className="space-y-4">
              <PreviewRow label="Investering" value="€ 11.790" />
              <PreviewRow label="Looptijd" value="10 jaar" />
              <PreviewRow label="Maandelijkse besparing" value="€ 187" accent="emerald" />
              <PreviewRow label="Maandbedrag financiering" value="€ 124" />
              <div className="pt-3 border-t border-white/10">
                <PreviewRow label="Netto in uw zak per maand" value="+ € 63" big />
              </div>
            </div>

            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-bold">
              <Leaf size={14} /> Vanaf dag 1 positieve cashflow
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const Stat = ({ tag, value, sub }) => (
  <div className="border-l-2 border-emerald-400/60 pl-3">
    <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">{tag}</div>
    <div className="font-heading text-2xl font-black leading-tight">{value}</div>
    <div className="text-[11px] text-slate-400">{sub}</div>
  </div>
);

const PreviewRow = ({ label, value, accent, big }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-sm text-slate-300">{label}</span>
    <span className={`font-heading font-black tabular-nums ${big ? "text-3xl gradient-pink-text" : "text-lg"} ${accent === "emerald" ? "text-emerald-400" : ""}`}>{value}</span>
  </div>
);

/* ---------------- FINANCING OPTIONS ---------------- */
const Options = () => {
  const opts = [
    {
      icon: CreditCard,
      tag: "Populair",
      name: "Helios Lening",
      lead: "Eigen eigendom, vaste rente",
      desc: "Klassieke duurzaamheidslening. U bent eigenaar vanaf dag 1, profiteert direct van alle subsidies en BTW-teruggave.",
      features: ["Eigen eigendom", "Vaste rente 4,9 - 6,9%", "Looptijd 5-15 jaar", "Inclusief BTW-teruggave", "Geen aanbetaling vereist"],
      cta: "Bekijk lening voorwaarden",
    },
    {
      icon: Repeat,
      tag: "Flexibel",
      name: "Solar Lease",
      lead: "Vast maandbedrag, geen zorgen",
      desc: "Wij blijven juridisch eigenaar; u betaalt een all-in maandbedrag inclusief onderhoud, monitoring en verzekering.",
      features: ["All-in maandbedrag", "Inclusief onderhoud", "Geen restwaarde-risico", "Optie tot overname", "Looptijd 10-15 jaar"],
      cta: "Vraag lease offerte",
    },
    {
      icon: Banknote,
      tag: "Eenvoudig",
      name: "Gespreid Betalen",
      lead: "0% rente, korte termijn",
      desc: "Verspreid de aankoop over 12 of 24 maanden, zonder rentekosten. Ideaal voor wie binnen 2 jaar wil aflossen.",
      features: ["0% rente bij ≤ 24 mnd", "Direct eigenaar", "Snel en eenvoudig", "Geen BKR-toetsing*"],
      cta: "Bekijk gespreid betalen",
    },
    {
      icon: Building2,
      tag: "Zakelijk",
      name: "PPA / Energy-as-a-Service",
      lead: "Voor ondernemers",
      desc: "U betaalt per opgewekte kWh, wij dragen investering en onderhoud. Direct positieve cashflow.",
      features: ["Geen CapEx", "Per kWh-tarief", "Inclusief monitoring", "Looptijd 10-20 jaar", "Geschikt vanaf 50 kWp"],
      cta: "Vraag PPA offerte",
    },
  ];

  return (
    <section className="section-pad bg-slate-50/70" data-testid="finance-options">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Financieringsopties</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Vier manieren om duurzaam te starten.</h2>
          <p className="mt-5 text-slate-600 text-lg">Onze adviseurs vergelijken kosteloos welke optie het beste rendement geeft voor uw situatie.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {opts.map((o, i) => (
            <motion.div
              key={o.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="relative bg-white border border-slate-200 rounded-3xl p-7 hover:border-brand/40 hover:shadow-[0_20px_50px_rgba(255,47,125,0.1)] hover:-translate-y-1 transition-all"
              data-testid={`finance-option-${i}`}
            >
              <span className="absolute -top-3 left-7 inline-flex px-3 py-1 rounded-full gradient-pink text-white text-xs font-bold tracking-wide shadow-[0_4px_14px_rgba(255,47,125,0.4)]">
                {o.tag}
              </span>
              <div className="w-12 h-12 rounded-2xl bg-brand-soft text-brand flex items-center justify-center mb-5">
                <o.icon size={22} />
              </div>
              <h3 className="font-heading font-black text-2xl text-navy-900">{o.name}</h3>
              <p className="text-sm text-brand-deep font-semibold mt-1">{o.lead}</p>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{o.desc}</p>

              <ul className="mt-5 space-y-2">
                {o.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={10} strokeWidth={3} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                state={{ calculator: { source: `finance_${o.name.toLowerCase().replace(/\s+/g, "_")}` } }}
                className="mt-7 inline-flex items-center gap-1 text-sm font-bold text-brand hover:text-brand-deep transition-colors"
                data-testid={`finance-option-cta-${i}`}
              >
                {o.cta} <ArrowRight size={14} />
              </Link>
            </motion.div>
          ))}
        </div>

        <p className="mt-7 text-center text-xs text-slate-500">* Onder voorbehoud van acceptatie. Toetsing afhankelijk van leningbedrag.</p>
      </div>
    </section>
  );
};

/* ---------------- CALCULATOR ---------------- */
const FinanceCalculator = () => {
  const [amount, setAmount] = useState([12000]);
  const [years, setYears] = useState([10]);
  const [type, setType] = useState("lening");

  const result = useMemo(() => {
    const rates = { lening: 0.054, lease: 0.069, gespreid: 0 };
    const r = rates[type];
    const n = years[0] * 12;
    const monthly = r === 0 ? amount[0] / Math.min(24, n) : (amount[0] * (r / 12)) / (1 - Math.pow(1 + r / 12, -n));
    // estimated monthly savings (rough model: solar+battery yields ~20% of investment annually first years)
    const monthlySaving = (amount[0] * 0.19) / 12;
    const net = monthlySaving - monthly;
    const totalCost = monthly * (r === 0 ? Math.min(24, n) : n);
    const interestPaid = totalCost - amount[0];
    return {
      monthly: Math.max(0, Math.round(monthly)),
      monthlySaving: Math.round(monthlySaving),
      net: Math.round(net),
      totalCost: Math.round(totalCost),
      interestPaid: Math.max(0, Math.round(interestPaid)),
    };
  }, [amount, years, type]);

  const types = [
    { key: "lening", label: "Lening", rate: "4,9-6,9%" },
    { key: "lease", label: "Lease", rate: "vast all-in" },
    { key: "gespreid", label: "Gespreid", rate: "0% (≤24mnd)" },
  ];

  return (
    <section id="finance-calculator" className="section-pad relative bg-navy-900 text-white overflow-hidden" data-testid="finance-calculator">
      <div className="absolute inset-0 noise-bg opacity-20" />
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-brand/30 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-emerald-500/20 blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400 mb-3">Maandlastencalculator</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-balance">Wat kost financieren? Wat levert het op?</h2>
          <p className="mt-5 text-slate-300 text-lg">Vergelijk direct uw maandbedrag met de maandelijkse besparing.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-7">
          <div className="lg:col-span-3 bg-white/5 backdrop-blur-xl border border-white/15 rounded-[2rem] p-7 lg:p-10">
            {/* Financing type */}
            <div>
              <label className="text-sm font-semibold mb-3 block">Financieringsvorm</label>
              <div className="grid grid-cols-3 gap-2">
                {types.map((tp) => (
                  <button
                    key={tp.key}
                    onClick={() => setType(tp.key)}
                    className={`relative rounded-2xl p-4 text-left border transition-all ${
                      type === tp.key
                        ? "bg-brand border-brand text-white shadow-[0_8px_24px_rgba(255,47,125,0.4)]"
                        : "bg-white/5 border-white/10 text-slate-300 hover:border-white/30"
                    }`}
                    data-testid={`finance-type-${tp.key}`}
                  >
                    <div className="font-heading font-bold">{tp.label}</div>
                    <div className="text-[11px] mt-0.5 opacity-80">{tp.rate}</div>
                  </button>
                ))}
              </div>
            </div>

            <Field label="Investeringsbedrag" value={`€ ${amount[0].toLocaleString("nl-NL")}`}>
              <Slider value={amount} onValueChange={setAmount} min={3000} max={40000} step={500} data-testid="finance-amount" />
            </Field>
            <Field label="Looptijd" value={`${years[0]} jaar`}>
              <Slider value={years} onValueChange={setYears} min={2} max={15} step={1} data-testid="finance-years" />
            </Field>

            <div className="mt-7 flex items-center gap-3 text-xs text-slate-400 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <Leaf size={16} className="text-emerald-400 flex-shrink-0" />
              <span>Indicatief, exacte voorwaarden afhankelijk van persoonlijke acceptatie en gekozen financier. Geen BKR-toetsing bij gespreid betalen tot 24 maanden.</span>
            </div>
          </div>

          <motion.div
            key={`${amount}-${years}-${type}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:col-span-2 bg-white text-navy-900 rounded-[2rem] p-7 lg:p-9 relative overflow-hidden"
            data-testid="finance-calc-results"
          >
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-emerald-100 opacity-60" />
            <div className="relative">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-2">Uw resultaat</p>
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Maandbedrag</div>
                <div className="font-heading text-5xl font-black gradient-pink-text">€ {result.monthly.toLocaleString("nl-NL")}</div>
              </div>

              <div className="mt-7 space-y-3">
                <Row k="Geschatte besparing/mnd" v={`€ ${result.monthlySaving.toLocaleString("nl-NL")}`} emerald />
                <Row k="Netto cashflow/mnd" v={`${result.net >= 0 ? "+ " : ""}€ ${result.net.toLocaleString("nl-NL")}`} bold />
                <div className="h-px bg-slate-200" />
                <Row k="Totale kosten" v={`€ ${result.totalCost.toLocaleString("nl-NL")}`} />
                <Row k="Waarvan rente" v={`€ ${result.interestPaid.toLocaleString("nl-NL")}`} />
              </div>

              <Link
                to="/contact"
                state={{ calculator: { source: "finance_calculator", amount: amount[0], years: years[0], type, monthly: result.monthly } }}
                className="mt-7 w-full btn-pink !text-white"
                data-testid="finance-calc-cta"
              >
                Ontvang offerte op maat <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, children }) => (
  <div className="mt-7">
    <div className="flex items-center justify-between mb-3">
      <label className="text-sm font-semibold">{label}</label>
      <span className="text-sm font-bold text-emerald-400 tabular-nums">{value}</span>
    </div>
    {children}
  </div>
);
const Row = ({ k, v, emerald, bold }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-sm text-slate-600">{k}</span>
    <span className={`font-heading tabular-nums ${bold ? "font-black text-xl" : "font-bold"} ${emerald ? "text-emerald-600" : "text-navy-900"}`}>{v}</span>
  </div>
);

/* ---------------- BENEFITS ---------------- */
const Benefits = () => {
  const items = [
    { icon: Sparkles, title: "Direct positieve cashflow", desc: "In de meeste gevallen is uw maandelijkse besparing hoger dan het maandbedrag." },
    { icon: Lock, title: "Vast rentepercentage", desc: "Bescherming tegen rente- en energieprijsstijgingen, vaste voorspelbare lasten." },
    { icon: BadgeCheck, title: "Inclusief alle subsidies", desc: "BTW-teruggave (21%) en eventuele provinciale subsidies worden volledig benut." },
    { icon: ShieldCheck, title: "10 jaar garantie", desc: "Productgarantie en installatiegarantie blijven 100% van toepassing." },
    { icon: TrendingUp, title: "Waardestijging woning", desc: "Zonnepanelen en batterij verhogen aantoonbaar de WOZ-waarde en verkoopwaarde." },
    { icon: Leaf, title: "100% duurzaam", desc: "Iedere kWh die u opwekt verlaagt direct uw CO₂-voetafdruk." },
  ];
  return (
    <section className="section-pad" data-testid="finance-benefits">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Voordelen</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Waarom financieren bij Helios Bright?</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-3xl p-7 hover:border-brand/40 hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-soft to-emerald-100 text-brand flex items-center justify-center mb-4">
                <it.icon size={20} />
              </div>
              <h3 className="font-heading font-bold text-lg text-navy-900 mb-2">{it.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- PROCESS ---------------- */
const Process = () => {
  const steps = [
    { n: "01", icon: Calculator, title: "Bereken maandbedrag", desc: "Vul uw gewenste investering en looptijd in. Direct duidelijkheid." },
    { n: "02", icon: Users, title: "Gratis adviesgesprek", desc: "Een Helios specialist analyseert uw woning en energieprofiel." },
    { n: "03", icon: FileSignature, title: "Offerte & financieringsplan", desc: "Heldere offerte met financieringsvergelijking, geen bindingen." },
    { n: "04", icon: Clock, title: "Installatie binnen 4 weken", desc: "Gecertificeerde monteurs leveren sleutelklaar op." },
    { n: "05", icon: Leaf, title: "Direct besparen", desc: "Vanaf maand 1 actief, én subsidies verwerkt." },
  ];
  return (
    <section className="section-pad bg-slate-50/70" data-testid="finance-process">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Hoe het werkt</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Van berekening tot besparing in 5 stappen.</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              className="relative bg-white border border-slate-200 rounded-3xl p-6 hover:border-brand/40 transition-all"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-heading text-4xl font-black gradient-pink-text">{s.n}</span>
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <s.icon size={18} />
                </div>
              </div>
              <h3 className="font-heading font-bold text-lg text-navy-900 mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- TRUST + REVIEWS ---------------- */
const TrustReviews = () => {
  const trustItems = [
    { icon: Star, value: "5.0 / 5", label: "Google reviews", color: "text-yellow-500" },
    { icon: ShieldCheck, value: "10 jaar", label: "Garantie", color: "text-brand" },
    { icon: BadgeCheck, value: "AFM", label: "Geregistreerde partners", color: "text-emerald-600" },
    { icon: Award, value: "2.500+", label: "Gefinancierde projecten", color: "text-brand" },
  ];
  const reviews = [
    { name: "Sander van Dijk", role: "Eigen woning · Apeldoorn", text: "Maandbedrag van € 118, bespaar er ongeveer € 195 op mijn energierekening. Zo eenvoudig kan het zijn.", rating: 5 },
    { name: "Femke de Boer", role: "Woningeigenaar · Almere", text: "Het hele financieringstraject was binnen 2 weken rond. Geen gedoe, alleen heldere stappen.", rating: 5 },
    { name: "Robin Hoekstra", role: "Familiewoning · Groningen", text: "Eindelijk een installateur die ook de financiering vergelijkt. Adviseur was eerlijk over voor- en nadelen.", rating: 5 },
  ];

  return (
    <section className="section-pad" data-testid="finance-trust">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid md:grid-cols-4 gap-4 mb-16">
          {trustItems.map((it, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 text-center hover:border-brand/40 hover:-translate-y-1 transition-all">
              <it.icon className={`mx-auto mb-3 ${it.color}`} size={28} strokeWidth={2} />
              <div className="font-heading font-black text-3xl text-navy-900">{it.value}</div>
              <div className="text-sm text-slate-500 mt-1">{it.label}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-gradient-to-br from-white to-emerald-50/40 border border-slate-200 rounded-3xl p-7 hover:border-brand/40 transition-all"
              data-testid={`finance-review-${i}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(r.rating)].map((_, x) => (
                  <Star key={x} className="fill-yellow-400 text-yellow-400" size={14} />
                ))}
              </div>
              <p className="text-navy-900 leading-relaxed">"{r.text}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full gradient-pink flex items-center justify-center font-heading font-black text-white text-sm">{r.name.charAt(0)}</div>
                <div>
                  <div className="font-semibold text-navy-900 text-sm">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- FAQ ---------------- */
const FAQ = () => {
  const faqs = [
    { q: "Heb ik een BKR-toetsing nodig?", a: "Bij een lening of lease boven € 1.000 wordt standaard BKR-toetsing gedaan door onze financieringspartner. Bij gespreid betalen tot 24 maanden meestal niet, afhankelijk van het bedrag." },
    { q: "Kan ik de financiering vervroegd aflossen?", a: "Ja, bij alle Helios Bright leningen kunt u boetevrij vervroegd aflossen. Bij lease kan een afkoopclausule van toepassing zijn, die staat altijd vóóraf in de overeenkomst." },
    { q: "Wat is het verschil tussen een lening en lease?", a: "Bij een lening bent u direct eigenaar en betaalt u een vast bedrag inclusief rente. Bij lease betaalt u een all-in maandbedrag inclusief onderhoud, maar u bent geen eigenaar (tenzij u na looptijd overneemt)." },
    { q: "Welke rente kan ik verwachten?", a: "Onze actuele tarieven liggen tussen 4,9% en 6,9% op jaarbasis, afhankelijk van looptijd, leningbedrag en uw acceptatieprofiel. Vraag een persoonlijke offerte aan voor uw exacte tarief." },
    { q: "Is mijn maandbedrag fiscaal aftrekbaar?", a: "Voor particulieren niet (geen renteaftrek meer voor consumptieve doeleinden). Voor zakelijke installaties wél: rente is aftrekbaar én er gelden EIA / MIA / Vamil regelingen, zie onze pagina Zakelijke Oplossing." },
    { q: "Wat gebeurt er als ik verhuis?", a: "Bij een lening loopt deze gewoon door, of u betaalt af. Bij lease kan de overeenkomst veelal worden overgedragen aan de nieuwe eigenaar, dat verhoogt vaak de verkoopwaarde." },
    { q: "Hoe snel kan de installatie plaatsvinden?", a: "Vanaf akkoord financiering tot installatie typisch 3 tot 5 weken, afhankelijk van planning en gemeentelijke melding." },
    { q: "Welke partners gebruikt Helios Bright?", a: "Wij werken samen met AFM-geregistreerde financiers gespecialiseerd in duurzame financiering, waaronder Greenloans, DUO+ en regionale duurzaamheidsfondsen." },
  ];
  return (
    <section className="section-pad bg-slate-50/70" data-testid="finance-faq">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">FAQ</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Veelgestelde vragen over financiering.</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white border border-slate-200 rounded-2xl px-6 data-[state=open]:border-brand/40 data-[state=open]:shadow-[0_10px_30px_rgba(255,47,125,0.08)] transition-all"
              data-testid={`finance-faq-item-${i}`}
            >
              <AccordionTrigger className="font-heading font-bold text-lg text-navy-900 hover:no-underline py-5 text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ---------------- CLOSING ---------------- */
const Closing = () => (
  <section className="py-24 relative overflow-hidden" data-testid="finance-closing">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="relative rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 p-10 lg:p-16 text-white">
        <div className="blob-pink absolute -top-32 -left-20 opacity-60" />
        <div className="absolute -bottom-32 -right-20 w-[500px] h-[500px] rounded-full bg-emerald-500/30 blur-[100px]" />

        <div className="relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400 mb-4">Klaar om te starten?</p>
            <h3 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-balance">Duurzaam wonen begint vandaag.<br />Niet morgen.</h3>
            <p className="text-slate-300 mt-6 text-lg max-w-lg">Vraag uw persoonlijke financieringsofferte aan en ontvang binnen 24 uur een heldere berekening.</p>
          </div>
          <div className="flex flex-col gap-4">
            <a href="#finance-calculator" className="btn-pink !text-white text-center" data-testid="finance-closing-cta-1">
              Bereken uw maandbedrag <ArrowRight size={18} />
            </a>
            <Link to="/contact" state={{ calculator: { source: "finance_offerte" } }} className="btn-ghost !bg-white/10 !text-white !border-white/20 hover:!bg-white/20 text-center" data-testid="finance-closing-cta-2">
              Ontvang offerte
            </Link>
            <Link to="/contact" state={{ calculator: { source: "finance_advies" } }} className="btn-ghost !bg-white/10 !text-white !border-white/20 hover:!bg-white/20 text-center" data-testid="finance-closing-cta-3">
              Vraag gratis advies aan
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */
const DuurzaamFinancieren = () => (
  <main data-testid="finance-page">
    <Hero />
    <Options />
    <FinanceCalculator />
    <Benefits />
    <Process />
    <TrustReviews />
    <FAQ />
    <Closing />
  </main>
);

export default DuurzaamFinancieren;
